import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

interface DDQ {
  id: string;
  firm_id: string;
  fund_id: string | null;
  type: 'firm' | 'fund';
  asset_class: string;
  status: 'draft' | 'submitted';
  allow_pdf: boolean;
  created_at: string;
  updated_at: string;
  fund_name?: string;
  access_count?: number;
  pending_requests?: number;
  pending_questions?: number;
}

interface AccessRequest {
  id: string;
  ddq_id: string;
  allocator_firm_id: string;
  status: string;
  requested_at: string;
  allocator_firm_name?: string;
  ddq_title?: string;
}

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  real_estate: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

export default function ManageDDQ() {
  const navigate = useNavigate();
  const { firm, profile } = useProfile();
  const [ddqs, setDdqs] = useState<DDQ[]>([]);
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'ddqs' | 'requests'>('ddqs');
  const [funds, setFunds] = useState<{ id: string; name: string; asset_class: string }[]>([]);
  const [showNewDDQ, setShowNewDDQ] = useState(false);
  const [newDDQType, setNewDDQType] = useState<'firm' | 'fund'>('firm');
  const [newDDQFund, setNewDDQFund] = useState('');
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  useEffect(() => {
    if (!firm) return;
    fetchData();
  }, [firm]);

  const fetchData = async () => {
    if (!firm) return;

    // Fetch DDQs
    const { data: ddqData } = await supabase
      .from('ddqs')
      .select('*')
      .eq('firm_id', firm.id)
      .order('created_at', { ascending: false });

    // Fetch funds
    const { data: fundsData } = await supabase
      .from('funds')
      .select('id, name, asset_class')
      .eq('firm_id', firm.id);
    if (fundsData) setFunds(fundsData);

    if (ddqData) {
      const enriched = await Promise.all(ddqData.map(async (ddq) => {
        let fund_name;
        if (ddq.fund_id && fundsData) {
          fund_name = fundsData.find(f => f.id === ddq.fund_id)?.name;
        }

        const { count: accessCount } = await supabase
          .from('ddq_access')
          .select('*', { count: 'exact', head: true })
          .eq('ddq_id', ddq.id)
          .eq('status', 'granted');

        const { count: pendingCount } = await supabase
          .from('ddq_access')
          .select('*', { count: 'exact', head: true })
          .eq('ddq_id', ddq.id)
          .eq('status', 'requested');

        const { count: questionCount } = await supabase
          .from('ddq_questions')
          .select('*', { count: 'exact', head: true })
          .eq('ddq_id', ddq.id)
          .is('answered_at', null);

        return {
          ...ddq,
          fund_name,
          access_count: accessCount || 0,
          pending_requests: pendingCount || 0,
          pending_questions: questionCount || 0,
        };
      }));
      setDdqs(enriched);
    }

    // Fetch access requests
    const { data: requestData } = await supabase
      .from('ddq_access')
      .select('*')
      .in('ddq_id', ddqData?.map(d => d.id) || [])
      .eq('status', 'requested')
      .order('requested_at', { ascending: false });

    if (requestData) {
      const enrichedRequests = await Promise.all(requestData.map(async (req) => {
        const { data: allocFirm } = await supabase
          .from('firms')
          .select('name')
          .eq('id', req.allocator_firm_id)
          .single();

        const ddq = ddqData?.find(d => d.id === req.ddq_id);
        const fund_name = ddq?.fund_id ? fundsData?.find(f => f.id === ddq.fund_id)?.name : null;
        const ddq_title = ddq ? `${ddq.type === 'firm' ? 'Firm DDQ' : `Fund DDQ, ${fund_name}`}` : 'DDQ';

        return {
          ...req,
          allocator_firm_name: allocFirm?.name || 'Unknown',
          ddq_title,
        };
      }));
      setAccessRequests(enrichedRequests);
    }

    setLoading(false);
  };

  const handleAccessRequest = async (requestId: string, action: 'granted' | 'declined') => {
    setProcessingRequest(requestId);
    await supabase.from('ddq_access').update({
      status: action,
      ...(action === 'granted' ? { granted_at: new Date().toISOString() } : {}),
    }).eq('id', requestId);
    setAccessRequests(prev => prev.filter(r => r.id !== requestId));
    setProcessingRequest(null);
    fetchData();
  };

  const handleCreateDDQ = async () => {
    if (!firm) return;
    const selectedFund = funds.find(f => f.id === newDDQFund);
    const assetClass = newDDQType === 'fund' && selectedFund
      ? selectedFund.asset_class
      : firm.type || 'hedge_fund';

    navigate(`/ddq/new?type=${newDDQType}&asset_class=${assetClass}${newDDQFund ? `&fund_id=${newDDQFund}` : ''}`);
  };

  const cardStyle = {
    background: '#FFFFFF', border: '1px solid #D0D5DD',
    borderRadius: '4px', padding: '20px', marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  };

  if (loading) return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6B7280' }}>Loading...</div>
    </div>
  );

  const totalPending = ddqs.reduce((s, d) => s + (d.pending_requests || 0), 0);
  const totalQuestions = ddqs.reduce((s, d) => s + (d.pending_questions || 0), 0);

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: '#0A0A0A', marginBottom: '4px' }}>
            DDQ Manager
          </h1>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>
            Manage your due diligence questionnaires and access requests
          </p>
        </div>
        <button
          onClick={() => setShowNewDDQ(true)}
          style={{
            background: '#0A1628', border: 'none', borderRadius: '3px',
            color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
            padding: '9px 18px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
        >+ New DDQ</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total DDQs', value: ddqs.length },
          { label: 'Submitted', value: ddqs.filter(d => d.status === 'submitted').length },
          { label: 'Access requests', value: totalPending, highlight: totalPending > 0 },
          { label: 'Unanswered questions', value: totalQuestions, highlight: totalQuestions > 0 },
        ].map((stat, i) => (
          <div key={i} style={{ ...cardStyle, marginBottom: 0 }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px' }}>{stat.label}</div>
            <div style={{
              fontFamily: 'Georgia, serif', fontSize: '28px',
              color: stat.highlight ? '#92640A' : '#0A0A0A'
            }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '4px', width: 'fit-content', marginBottom: '20px' }}>
        {[
          { key: 'ddqs', label: 'My DDQs' },
          { key: 'requests', label: `Access Requests${totalPending > 0 ? ` (${totalPending})` : ''}` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)} style={{
            padding: '6px 16px', borderRadius: '3px', fontSize: '13px',
            cursor: 'pointer', border: 'none',
            background: activeTab === tab.key ? '#F5F6F8' : 'transparent',
            color: activeTab === tab.key ? '#0A0A0A' : '#6B7280',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'ddqs' && (
        <>
          {ddqs.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>📋</div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: '#0A0A0A', marginBottom: '8px' }}>
                No DDQs yet
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px' }}>
                Create your first DDQ to share with allocators during due diligence
              </div>
              <button onClick={() => setShowNewDDQ(true)} style={{
                background: '#0A1628', border: 'none', borderRadius: '3px',
                color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
                padding: '10px 24px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>Create DDQ</button>
            </div>
          ) : (
            ddqs.map(ddq => (
              <div key={ddq.id} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: '#0A0A0A' }}>
                        {ddq.type === 'firm' ? 'Firm DDQ' : `Fund DDQ, ${ddq.fund_name}`}
                      </span>
                      <span style={{
                        fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
                        background: ddq.status === 'submitted' ? '#0A7C4215' : '#92640A15',
                        color: ddq.status === 'submitted' ? '#0A7C42' : '#92640A',
                      }}>{ddq.status === 'submitted' ? '✓ Submitted' : 'Draft'}</span>
                      {ddq.allow_pdf && (
                        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: '#0D6E8A15', color: '#0D6E8A' }}>
                          PDF enabled
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                      {classLabels[ddq.asset_class]} · Updated {new Date(ddq.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        <span style={{ color: '#0A0A0A', fontWeight: 500 }}>{ddq.access_count}</span> allocators with access
                      </div>
                      {(ddq.pending_requests || 0) > 0 && (
                        <div style={{ fontSize: '12px', color: '#92640A' }}>
                          <span style={{ fontWeight: 500 }}>{ddq.pending_requests}</span> pending requests
                        </div>
                      )}
                      {(ddq.pending_questions || 0) > 0 && (
                        <div style={{ fontSize: '12px', color: '#92640A' }}>
                          <span style={{ fontWeight: 500 }}>{ddq.pending_questions}</span> unanswered questions
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
                    <button
                      onClick={() => navigate(`/ddq/new?ddq_id=${ddq.id}&type=${ddq.type}&asset_class=${ddq.asset_class}${ddq.fund_id ? `&fund_id=${ddq.fund_id}` : ''}`)}
                      style={{
                        background: '#FFFFFF', border: '1px solid #D0D5DD',
                        borderRadius: '3px', color: '#3D4552', fontSize: '12px',
                        padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}
                    >{ddq.status === 'submitted' ? 'View' : 'Edit'}</button>
                    <button
                      onClick={() => navigate(`/ddq/${ddq.id}/access`)}
                      style={{
                        background: '#0A1628', border: '1px solid #0A1628',
                        borderRadius: '3px', color: '#FFFFFF', fontSize: '12px',
                        padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}
                    >Manage access</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {activeTab === 'requests' && (
        <>
          {accessRequests.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '60px' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>No pending access requests</div>
            </div>
          ) : (
            accessRequests.map(req => (
              <div key={req.id} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#0A0A0A', marginBottom: '4px' }}>
                      {req.allocator_firm_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      Requested access to {req.ddq_title} · {new Date(req.requested_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleAccessRequest(req.id, 'declined')}
                      disabled={processingRequest === req.id}
                      style={{
                        background: '#FFFFFF', border: '1px solid #D0D5DD',
                        borderRadius: '3px', color: '#C0222A', fontSize: '12px',
                        padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}
                    >Decline</button>
                    <button
                      onClick={() => handleAccessRequest(req.id, 'granted')}
                      disabled={processingRequest === req.id}
                      style={{
                        background: '#0A1628', border: '1px solid #0A1628',
                        borderRadius: '3px', color: '#FFFFFF', fontSize: '12px',
                        padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}
                    >Grant access</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* New DDQ Modal */}
      {showNewDDQ && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#FFFFFF', border: '1px solid #D0D5DD',
            borderRadius: '4px', padding: '28px', width: '420px', maxWidth: '90vw'
          }}>
            <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: '#0A0A0A', marginBottom: '20px' }}>
              Create new DDQ
            </h3>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', display: 'block' }}>DDQ TYPE</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['firm', 'fund'] as const).map(t => (
                  <button key={t} onClick={() => setNewDDQType(t)} style={{
                    flex: 1, padding: '10px', borderRadius: '3px', fontSize: '13px',
                    cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                    border: `1px solid ${newDDQType === t ? '#C9A84C' : '#D0D5DD'}`,
                    background: newDDQType === t ? '#FDF6E7' : 'transparent',
                    color: newDDQType === t ? '#0A1628' : '#6B7280',
                    textTransform: 'capitalize',
                  }}>{t}-level DDQ</button>
                ))}
              </div>
            </div>

            {newDDQType === 'fund' && funds.length > 0 && (
              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', display: 'block' }}>SELECT FUND</label>
                <select
                  value={newDDQFund}
                  onChange={e => setNewDDQFund(e.target.value)}
                  style={{
                    width: '100%', background: '#FFFFFF', border: '1px solid #D0D5DD',
                    borderRadius: '3px', color: '#0A0A0A', fontSize: '13px', padding: '10px 14px',
                    outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  <option value="">Select a fund...</option>
                  {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={() => setShowNewDDQ(false)} style={{
                flex: 1, background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '3px', color: '#3D4552', padding: '10px',
                cursor: 'pointer', fontSize: '13px', fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>Cancel</button>
              <button
                onClick={handleCreateDDQ}
                disabled={newDDQType === 'fund' && !newDDQFund}
                style={{
                  flex: 1, background: '#0A1628', border: 'none', borderRadius: '3px',
                  color: '#FFFFFF', padding: '10px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600, fontFamily: 'system-ui, -apple-system, sans-serif',
                  opacity: newDDQType === 'fund' && !newDDQFund ? 0.5 : 1
                }}
              >Create DDQ</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}